import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────

export interface FleetDefaultTruckType {
  id: string;
  name: string;
  defaultCapacity: number;
  defaultWeightLimit: number;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TruckTypeInput {
  name: string;
  defaultCapacity: number;
  defaultWeightLimit: number;
  active?: boolean;
}

export type FuelType = 'gas' | 'diesel' | 'electric' | 'hybrid';

export interface FleetDefaultMakeModel {
  id: string;
  make: string;
  model: string;
  compatibleTruckTypes: string[];
  fuelTypeDefaults: FuelType[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MakeModelInput {
  make: string;
  model: string;
  compatibleTruckTypes: string[];
  fuelTypeDefaults?: FuelType[];
  active?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useFleetDefaultTruckTypes() {
  const [items, setItems] = useState<FleetDefaultTruckType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: FleetDefaultTruckType[] }>(
        '/admin/fleet-defaults/truck-types',
      );
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load truck types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: TruckTypeInput): Promise<FleetDefaultTruckType> => {
    const res = await api.post<{ success: boolean; data: FleetDefaultTruckType }>(
      '/admin/fleet-defaults/truck-types',
      input,
    );
    await fetchItems();
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<TruckTypeInput>,
  ): Promise<FleetDefaultTruckType> => {
    const res = await api.patch<{ success: boolean; data: FleetDefaultTruckType }>(
      `/admin/fleet-defaults/truck-types/${id}`,
      input,
    );
    setItems(prev => prev.map(item => (item.id === id ? res.data : item)));
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/admin/fleet-defaults/truck-types/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const reorderItems = async (orderedIds: string[]): Promise<void> => {
    await api.post('/admin/fleet-defaults/truck-types/reorder', {
      order: orderedIds.map(id => ({ id })),
    });
    await fetchItems();
  };

  return {
    items,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    refetch: fetchItems,
  };
}

// ── Makes & Models Hook ───────────────────────────────────────────────────

export function useFleetDefaultMakesModels() {
  const [items, setItems] = useState<FleetDefaultMakeModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: FleetDefaultMakeModel[] }>(
        '/admin/fleet-defaults/makes-models',
      );
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load makes/models');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: MakeModelInput): Promise<FleetDefaultMakeModel> => {
    const res = await api.post<{ success: boolean; data: FleetDefaultMakeModel }>(
      '/admin/fleet-defaults/makes-models',
      input,
    );
    await fetchItems();
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<MakeModelInput>,
  ): Promise<FleetDefaultMakeModel> => {
    const res = await api.patch<{ success: boolean; data: FleetDefaultMakeModel }>(
      `/admin/fleet-defaults/makes-models/${id}`,
      input,
    );
    setItems(prev => prev.map(item => (item.id === id ? res.data : item)));
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/admin/fleet-defaults/makes-models/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  return {
    items,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    refetch: fetchItems,
  };
}

// ── Download & Upload ─────────────────────────────────────────────────────

export interface UploadSummary {
  truckTypesCreated: number;
  truckTypesUpdated: number;
  makesModelsCreated: number;
  makesModelsUpdated: number;
  errors: string[];
}

export async function downloadFleetDefaultsJson(): Promise<void> {
  const res = await api.get<{ success: boolean; data: Record<string, unknown> }>(
    '/admin/fleet-defaults/download',
  );
  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'fleet-defaults.json';
  a.click();
  URL.revokeObjectURL(url);
}

export async function uploadFleetDefaultsJson(
  data: Record<string, unknown>,
): Promise<UploadSummary> {
  const res = await api.post<{ success: boolean; data: UploadSummary }>(
    '/admin/fleet-defaults/upload',
    data,
  );
  return res.data;
}
